import styled from "styled-components";
import {useAppDispatch} from "../../../../hooks";
import {updateCurrentRecord} from "../../../../features/tableSlice";
import {apiFetch, confirmAction} from "../../../util";
import React, {useCallback, useEffect, useState} from "react";
import {PhpType} from "../../../../features/schemaManageSlice";
import Loader from "../../../Loader";
import {Field, Primitive, PrimitiveObject} from "../../../../Types";
import EditColumnRow from "./EditColumnRow";
import {useApiFetch} from "../../../hooks/useApiFetch";
import {SqlQuery} from "../../../schemaManage/back/BackTableFieldViewTable";
import {toast} from "react-toastify";


type PropsI = {
    pkValue: string | number,
    backendUrl: string,
    fields: Field[];
    project: string,
    tableKey: string,
    closeEdit: () => void
};
export type EditValueI = {
    value: Primitive,
    format: "const" | "expression" | "null"
    isEdited: boolean;
    invalid: boolean;
};

export default function EditTableRecord({pkValue, backendUrl, project, tableKey, fields, closeEdit}: PropsI) {
    const [schema, setSchema] = useState<Record<string, ColumnInfo>>();
    const [currentValue, setCurrentValue] = useState<PrimitiveObject>();
    const [newValue, setNewValue] = useState<Record<string, EditValueI>>();
    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const {fetchResult, loading, success, error} = useApiFetch<{
        data: {
            schema: ColumnInfo[];
            value: PrimitiveObject;
        }
    }>(`${backendUrl}/astra/getRawEditSchema.php?project=${project}&table=${tableKey}&pk=${pkValue}`);

    // Map API data into state
    useEffect(() => {
        if (!fetchResult || !success) return;
        const data = fetchResult.data;
        const schemaMap = Object.fromEntries(
            data.schema.map(col => [col.column_name, col])
        ) as Record<string, ColumnInfo>;

        const newValMap = Object.fromEntries(
            Object.entries(data.value).map(([key, value]) => [
                key,
                {value: value as Primitive, format: "const", isEdited: false, invalid: false}
            ])
        ) as Record<string, EditValueI>;

        setSchema(schemaMap);
        setCurrentValue(data.value);
        setNewValue(newValMap);
    }, [fetchResult, success]);

    const updateEntry = async () => await confirmAction({
        message: "Оновити поля?",
        onConfirm: async () => {
            const edited = Object.entries(newValue!).filter(x => x[1].isEdited);
            if (edited.length < 1) {
                toast.error("Нема редагованих полів")
                return
            }
            setIsUpdating(true)
            const {success, body} = await apiFetch(`${backendUrl}/achillea/rawEditRecord.php`, {
                method: "PATCH", body: JSON.stringify({
                    project: project,
                    table: tableKey,
                    pk: pkValue,
                    record: Object.fromEntries(
                        edited.map(([key, val]) => [key, {
                                format: val.format,
                                value: val.value,
                                type: schema![key].php_type
                            }])
                    )
                })
            });
            setIsUpdating(false)
            if (success) {
                const pkField = Object.values(schema!).find(s => s.is_primary_key);
                dispatch(updateCurrentRecord({pkKey: pkField!.column_name, record: body.data.record}))
                toast.success(body.message);
                closeEdit()
            }
        }
    });

    const setValue = useCallback((column: string, curr: EditValueI) => {
        setNewValue(prev => ({...prev, [column]: curr}));
    }, []);

    return (
        <Container>
            {loading && <Loader/>}
            {error && <p style={{color: "red"}}>Error: {error}</p>}

            {schema && currentValue && newValue && (
                <table>
                    <thead>
                    <tr className="tr-head">
                        <th>Поле</th>
                        <th>Формат</th>
                        <th>Значення</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.values(schema).map(s => (
                        <EditColumnRow
                            key={s.column_name}
                            setValue={setValue}
                            info={s}
                            schema={fields.find(x => x.key === s.column_name)}
                            currentValue={currentValue[s.column_name]}
                            value={newValue[s.column_name]}
                        />
                    ))}
                    </tbody>
                    <tfoot>
                    <tr className="tr-head">
                        <td colSpan={3}>
                            Усього полів: {Object.values(currentValue).length}
                        </td>
                    </tr>
                    </tfoot>
                </table>
            )}
            <div className="summary">
                <div className="new-vals">
                    {newValue && currentValue && Object.entries(newValue).filter(x => x[1].isEdited)
                        .map(([key, val]) => <div className={"edit-val " + (val.invalid ? "inv" : "")} key={key}>
                            <div className="key">{key}: {String(currentValue[key])}</div>
                            <div className="arrow">={">"}</div>
                            <div className="val">{
                                val.format === "expression" ?
                                    <SqlQuery sqlQuery={val.value as string}/> :
                                    <NewVal val={val.value}/>
                            }</div>
                        </div>)}
                </div>
                <div className="edit-controls">
                    <button className={'btn-cmn'} onClick={updateEntry} disabled={isUpdating}>
                        Оновити поля
                    </button>
                </div>
            </div>
        </Container>
    );
}

const NewVal = ({val}: { val: Primitive }) => {

    if (val === null)
        return <i className={"null-value"}>NULL</i>;

    if (val === '')
        return <i>*порожній рядок*</i>

    return <>{String(val)}</>
}


const Container = styled.div`
    background-color: var(--table-row-odd-bg-color);
    padding: .5rem;
    border-radius: .5rem;
    width: 100%;
    height: 100%;
    display: flex;
    flex-flow: column;
    gap: .5rem;

    & .summary {
        display: flex;
        gap: .5rem;
        flex-flow: column;

        & .edit-controls {
            padding-bottom: .5rem;

            & button {
                width: 100%;
            }
        }

        & .new-vals {
            display: flex;
            gap: .5rem;
            flex-flow: column;

            & .inv {
                border-color: red;
            }

            & .edit-val {
                display: flex;
                gap: .5rem;
                flex-flow: wrap;
                align-items: center;
                border-radius: .2rem;
                padding: .2rem;
                border: 2px solid var(--table-border-color)
            }
        }
    }


    & .tr-head {
        border-top: 2px solid var(--table-border-color);
        border-bottom: 2px solid var(--table-border-color);
        background: var(--table-header-bg-color);
    }

    & td, th {
        border-left: 2px solid var(--table-border-color);
        border-right: 2px solid var(--table-border-color);
    }


    .controls {
        display: flex;
        justify-content: end;
        gap: .5rem;
    }

    .check {
        display: flex;
        margin-top: auto;
        gap: .5rem;
    }

    & .entries {
        display: flex;
        flex-flow: column;
        gap: .5rem;
        justify-content: center;
    }

    .loader {
        width: 100%;
        height: 500px;
    }
`


export interface ColumnInfo {
    column_name: string;
    data_type: string;
    php_type: PhpType;
    not_null: boolean;
    column_default: string | null;
    comment: string | null;
    is_primary_key: boolean;
    is_foreign_key: boolean;
    foreign_schema: string | null;
    foreign_table: string | null;
    foreign_column: string | null;
    expr: string | null;
    expr_type: string | null;
}
